import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../layouts/AuthLayout';
import { Alert, Field } from '../components/Field';
import { api, type ApiError } from '../api/client';

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get('token') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <h2 className="auth__title">Choose a new password</h2>
      <p className="auth__lede">
        Every signed-in session will be ended, so you will sign in again everywhere.
      </p>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}

      <form onSubmit={onSubmit} noValidate>
        {params.get('token') ? null : (
          <Field
            label="Reset token"
            required
            value={token}
            onChange={(event) => setToken(event.target.value)}
            hint="Paste the token from your reset email."
          />
        )}
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 10 characters, using three of: lowercase, uppercase, digit, symbol."
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set new password'}
        </button>
      </form>

      <p className="auth__footnote">
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}
